import { useMemo, useState } from "react";
import { CaretDown, CaretRight, ArrowRight, ArrowLeft, Handshake, ArrowUUpLeft } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ownerTripBalance, computeChauffeurSplit } from "../lib/chauffeurSplit";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OwnerBalancePanel({ bookings = [], vehicles = [], chauffeurs = [], onChange }) {
  const [open, setOpen] = useState({});
  const [openSettled, setOpenSettled] = useState({});
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({}); // booking_id -> bool (settle selection)
  const vmap = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v])), [vehicles]);
  const dmap = useMemo(() => Object.fromEntries(chauffeurs.map((d) => [d.id, d])), [chauffeurs]);

  const owners = useMemo(() => {
    const groups = {};
    for (const b of bookings) {
      if (!["chauffeur", "tour"].includes(b.booking_type)) continue;
      if (b.status !== "completed") continue;
      // Trips paid out directly through the driver flow (not via an owner
      // settle) are closed on our side — hide them from the open balance.
      if (b.driver_paid && !b.owner_settled) continue;
      const v = vmap[b.vehicle_id];
      if (!v || v.ownership !== "third_party") continue;
      const d = dmap[b.driver_id];
      const key = (v.owner_name || "").trim() || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() || "Unknown owner";
      const s = computeChauffeurSplit(b, v, d);
      const bal = ownerTripBalance(b, v, d);
      const t = { ...b, _v: v, _total: s.total, _weOwe: bal.weOwe, _theyOwe: bal.theyOwe, _override: !!bal.override };
      const g = (groups[key] = groups[key] || { owner: key, trips: [], settledTrips: [], weOwe: 0, theyOwe: 0 });
      if (b.owner_settled) {
        g.settledTrips.push(t);
        continue;
      }
      g.weOwe += t._weOwe;
      g.theyOwe += t._theyOwe;
      g.trips.push(t);
    }
    return Object.values(groups)
      .filter((g) => g.trips.length > 0 || g.settledTrips.length > 0)
      .map((g) => ({ ...g, net: g.weOwe - g.theyOwe }))
      .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
  }, [bookings, vmap, dmap]);

  const totals = owners.reduce((s, o) => ({ weOwe: s.weOwe + o.weOwe, theyOwe: s.theyOwe + o.theyOwe }), { weOwe: 0, theyOwe: 0 });
  const netTotal = totals.weOwe - totals.theyOwe;

  const settle = async (o, ids) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Settle up with ${o.owner}? This clears ${ids.length} trip(s), resets their balance, and moves them to Paid out Chauffeurs.`)) return;
    setBusy(true);
    try {
      await api.post("/owners/settle", { booking_ids: ids, settled: true });
      toast.success(`Settled ${ids.length} trip(s) with ${o.owner}`);
      setSel({});
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const unsettle = async (o) => {
    const ids = o.settledTrips.map((t) => t.id);
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await api.post("/owners/settle", { booking_ids: ids, settled: false });
      toast.success(`Reopened ${ids.length} settled trip(s) for ${o.owner}`);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (owners.length === 0) {
    return (
      <div className="border border-border bg-card p-12 text-center text-muted-foreground" data-testid="owner-balance-empty">
        No completed 3rd-party owned chauffeur/tour trips yet. Set a vehicle&apos;s ownership to “3rd-party” in Fleet to track owner balances here.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="owner-balance-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard label="We owe owners" value={money(totals.weOwe)} tone="amber" testid="owner-total-we-owe" />
        <SummaryCard label="Owners owe us" value={money(totals.theyOwe)} tone="emerald" testid="owner-total-they-owe" />
        <SummaryCard label="Net position" value={`${netTotal >= 0 ? "We owe " : "Owed to us "}${money(Math.abs(netTotal))}`} tone={netTotal >= 0 ? "amber" : "emerald"} testid="owner-net" />
      </div>

      <div className="border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 label-mono font-normal">Owner</th>
              <th className="text-left px-4 py-3 label-mono font-normal">Trips</th>
              <th className="text-right px-4 py-3 label-mono font-normal">We owe them</th>
              <th className="text-right px-4 py-3 label-mono font-normal">They owe us</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Net balance</th>
              <th className="text-right px-4 py-3 label-mono font-normal w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {owners.map((o) => {
              const selectedTrips = o.trips.filter((t) => sel[t.id]);
              const selNet = selectedTrips.reduce((s, t) => s + t._weOwe - t._theyOwe, 0);
              return (
              <>
                <tr key={o.owner} className="border-b border-border/50 hover:bg-accent/30" data-testid={`owner-row-${o.owner}`}>
                  <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => setOpen((p) => ({ ...p, [o.owner]: !p[o.owner] }))}>
                    <span className="inline-flex items-center gap-1.5">
                      {open[o.owner] ? <CaretDown size={14} /> : <CaretRight size={14} />}{o.owner}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{o.trips.length}</td>
                  <td className="px-4 py-3 text-right text-amber-400">{money(o.weOwe)}</td>
                  <td className="px-4 py-3 text-right text-emerald-400">{money(o.theyOwe)}</td>
                  <td className={`px-4 py-3 text-right font-heading ${o.net >= 0 ? "text-amber-400" : "text-emerald-400"}`}>
                    {o.trips.length === 0 ? "—" : <>{o.net >= 0 ? "We owe " : "Owed to us "}{money(Math.abs(o.net))}</>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" disabled={busy || o.trips.length === 0} onClick={() => settle(o, o.trips.map((t) => t.id))} data-testid={`owner-settle-${o.owner}`}>
                      <Handshake size={14} className="mr-1 text-emerald-500" />Settle up
                    </Button>
                  </td>
                </tr>
                {open[o.owner] && o.trips.length > 0 && (
                  <tr className="bg-muted/20">
                    <td colSpan={6} className="px-4 py-2">
                      <div className="divide-y divide-border/40">
                        {o.trips.map((t) => (
                          <div key={t.id} className="flex items-center justify-between gap-3 py-2 text-xs flex-wrap" data-testid={`owner-trip-${t.id}`}>
                            <input
                              type="checkbox"
                              checked={!!sel[t.id]}
                              onChange={(e) => setSel((p) => ({ ...p, [t.id]: e.target.checked }))}
                              data-testid={`owner-trip-check-${t.id}`}
                            />
                            <span className="text-muted-foreground w-24">{t.start_datetime ? new Date(t.start_datetime).toLocaleDateString() : (t.booking_date || "—")}</span>
                            <span className="flex-1 min-w-[140px]">
                              {t.customer_name || "—"} · {t.vehicle_label || `${t._v.make} ${t._v.model}`}
                              {t.split_confirmed && <span className="text-emerald-500 ml-1.5" title="Split confirmed">✓ split</span>}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              {t._theyOwe > 0
                                ? <><ArrowLeft size={12} className="text-emerald-400" /> owes us {money(t._theyOwe)}</>
                                : t._weOwe > 0
                                  ? <><ArrowRight size={12} className="text-amber-400" /> we owe {money(t._weOwe)}</>
                                  : <span className="text-muted-foreground">even — $0.00</span>}
                              {t._override && <span className="text-[10px] text-muted-foreground">(override)</span>}
                            </span>
                            <span className="text-muted-foreground w-24 text-right">total {money(t._total)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-end gap-2 pt-2 mt-1 border-t border-border/40">
                        <span className="text-xs text-muted-foreground">
                          {selectedTrips.length > 0
                            ? <>{selectedTrips.length} selected · net {selNet >= 0 ? "we owe" : "owed to us"} {money(Math.abs(selNet))}</>
                            : "Select trips to settle just those"}
                        </span>
                        <Button size="sm" disabled={busy || selectedTrips.length === 0} onClick={() => settle(o, selectedTrips.map((t) => t.id))} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`owner-settle-selected-${o.owner}`}>
                          <Handshake size={14} className="mr-1" />Settle selected ({selectedTrips.length})
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                {o.settledTrips.length > 0 && (
                  <tr className="bg-muted/10">
                    <td colSpan={6} className="px-4 py-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <button className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={() => setOpenSettled((p) => ({ ...p, [o.owner]: !p[o.owner] }))} data-testid={`owner-settled-toggle-${o.owner}`}>
                          {openSettled[o.owner] ? <CaretDown size={12} /> : <CaretRight size={12} />}
                          Settled history ({o.settledTrips.length})
                        </button>
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => unsettle(o)} data-testid={`owner-unsettle-${o.owner}`}>
                          <ArrowUUpLeft size={13} className="mr-1 text-amber-500" />Undo settle
                        </Button>
                      </div>
                      {openSettled[o.owner] && (
                        <div className="divide-y divide-border/30 mt-2 opacity-70">
                          {o.settledTrips.map((t) => (
                            <div key={t.id} className="flex items-center justify-between gap-3 py-2 text-xs flex-wrap" data-testid={`owner-settled-trip-${t.id}`}>
                              <span className="text-muted-foreground w-28">{t.start_datetime ? new Date(t.start_datetime).toLocaleDateString() : (t.booking_date || "—")}</span>
                              <span className="flex-1 min-w-[140px]">{t.customer_name || "—"} · {t.vehicle_label || `${t._v.make} ${t._v.model}`}</span>
                              <span className="inline-flex items-center gap-1 text-emerald-400">settled{t.owner_settled_at ? ` · ${new Date(t.owner_settled_at).toLocaleDateString()}` : ""}</span>
                              <span className="text-muted-foreground w-24 text-right">total {money(t._total)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            );})}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Balances use the exact same math as each booking&apos;s split section (deposit/balance held-by, driver fee, expenses fronted, overrides). “Settle up” clears the running balance, moves those trips to settled history, and marks them Paid out in the Chauffeur tabs.
      </p>
    </div>
  );
}

function SummaryCard({ label, value, tone, testid }) {
  const tones = { amber: "text-amber-400", emerald: "text-emerald-400" };
  return (
    <div className="border border-border bg-card p-4">
      <div className="label-mono text-xs">{label}</div>
      <div className={`font-heading text-xl mt-1 ${tones[tone] || ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}
